import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { Col, Row, Space } from 'antd';
import { LeftOutlined, RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import {
  Button,
  Empty,
  InputNumber,
  message,
  Modal,
  PopConfirm,
  SubContent,
  Table,
  Tooltip,
} from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { ElectricityContractVo } from '../../../../../api/electricity';
import {
  apiV2ElectricityContractMarketPriceBatchAddPost,
  apiV2ElectricityContractMarketPriceCopyToMonthPost,
  apiV2ElectricityContractMarketPriceDetailPost,
} from '@maxtropy/device-customer-apis-v2';
import CopyToMonthModal from './components/CopyToMonthModal';
import styles from './index.module.scss';

// 生成96个时段（每15分钟一个）
const generateTimeSlots = () => {
  const slots: string[] = [];
  for (let i = 0; i < 96; i++) {
    const hours = Math.floor((i * 15) / 60);
    const minutes = (i * 15) % 60;
    const startHour = hours.toString().padStart(2, '0');
    const startMinute = minutes.toString().padStart(2, '0');
    const endHours = Math.floor(((i + 1) * 15) / 60) % 24;
    const endMinutes = ((i + 1) * 15) % 60;
    const endHour = endHours.toString().padStart(2, '0');
    const endMinute = endMinutes.toString().padStart(2, '0');
    slots.push(`${startHour}:${startMinute}-${endHour}:${endMinute}`);
  }
  return slots;
};

const timeSlots = generateTimeSlots();

interface PriceData {
  [date: string]: string[]; // 每个日期对应96个单价
}

interface ConfiguredDates {
  [date: string]: boolean; // 每个日期是否已配置
}

interface StateGridRateProps {
  contract?: ElectricityContractVo;
  disabled?: boolean;
}

const StateGridRate: FC<StateGridRateProps> = ({ contract, disabled = false }) => {
  const [currentMonth, setCurrentMonth] = useState<Dayjs>(dayjs());
  const [selectedDates, setSelectedDates] = useState<string[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isBatchMode, setIsBatchMode] = useState(false);
  const [priceData, setPriceData] = useState<PriceData>({});
  const [configuredDates, setConfiguredDates] = useState<ConfiguredDates>({});
  const [tempPriceData, setTempPriceData] = useState<(number | undefined)[]>([]);
  const [copyToMonthModalVisible, setCopyToMonthModalVisible] = useState(false);

  // 获取月份费率详情
  const { run: fetchMonthDetail } = useRequest(
    (params: Parameters<typeof apiV2ElectricityContractMarketPriceDetailPost>[0]) =>
      apiV2ElectricityContractMarketPriceDetailPost(params),
    {
      manual: true,
      onSuccess: res => {
        if (res?.days) {
          const newPriceData: PriceData = {};
          const newConfiguredDates: ConfiguredDates = {};
          res.days.forEach(day => {
            if (day.date && day.periods) {
              const dateStr = dayjs(day.date).format('YYYY-MM-DD');
              // periods 按 period 排序，取 price
              const prices = Array.from({ length: 96 }, (_, i) => {
                const period = day.periods?.find(p => p.period === i + 1);
                return period?.price?.toString() ?? '';
              });
              newPriceData[dateStr] = prices;
              // 记录已配置的日期
              if (day.configured) {
                newConfiguredDates[dateStr] = true;
              }
            }
          });
          setPriceData(prev => ({ ...prev, ...newPriceData }));
          setConfiguredDates(prev => ({ ...prev, ...newConfiguredDates }));
        }
      },
    }
  );

  // 当月份或合同变化时获取详情
  useEffect(() => {
    if (contract?.id) {
      fetchMonthDetail({
        electricityContractId: contract.id,
        monthTimestamp: currentMonth.startOf('month').valueOf(),
      });
    }
  }, [contract?.id, currentMonth, fetchMonthDetail]);

  // 获取当月的所有日期
  const getMonthDates = useCallback((month: Dayjs) => {
    const start = month.startOf('month');
    const end = month.endOf('month');
    const dates: Dayjs[] = [];
    let current = start;
    while (current.isBefore(end) || current.isSame(end, 'date')) {
      dates.push(current);
      current = current.add(1, 'day');
    }
    return dates;
  }, []);

  // 获取日历网格（根据每月1号星期几对齐，前面用空白占位）
  const calendarGrid = useMemo(() => {
    const monthDates = getMonthDates(currentMonth);
    const firstDay = monthDates[0].day(); // 周日为0
    // 添加空白占位，让日期对齐到正确的星期几
    const emptySlots: (Dayjs | null)[] = new Array(firstDay).fill(null);
    return [...emptySlots, ...monthDates];
  }, [currentMonth, getMonthDates]);

  // 判断日期是否在合同有效期内
  const isDateInContract = useCallback(
    (date: Dayjs) => {
      if (!contract) return false;
      return date.isBetween(
        dayjs(contract.startDate).startOf('date'),
        dayjs(contract.endDate).endOf('date'),
        null,
        '[]'
      );
    },
    [contract]
  );

  // 判断日期是否属于当前月份
  const isCurrentMonth = useCallback(
    (date: Dayjs) => {
      return date.month() === currentMonth.month() && date.year() === currentMonth.year();
    },
    [currentMonth]
  );

  // 切换日期选择
  const toggleDate = useCallback(
    (date: Dayjs) => {
      if (!isCurrentMonth(date)) return;
      // 如果有合同且日期不在合同范围内，则不允许点击
      if (contract && !isDateInContract(date)) return;
      const dateStr = date.format('YYYY-MM-DD');

      // 在查看模式下，也允许点击日期，但只在非批量模式下（即可以查看不同日期的数据）
      // 在编辑模式下，如果disabled则不允许操作
      if (disabled && isBatchMode) return;

      if (isBatchMode) {
        // 批量配置模式可以多选
        setSelectedDates(prev => (prev.includes(dateStr) ? prev.filter(d => d !== dateStr) : [...prev, dateStr]));
      } else {
        // 非批量配置模式只能单选，用于查看
        setSelectedDates([dateStr]);
      }
    },
    [isCurrentMonth, isDateInContract, disabled, isBatchMode, contract]
  );

  // 当选中日期变化时，加载对应日期的价格数据
  useEffect(() => {
    if (selectedDates.length === 1) {
      const date = selectedDates[0];
      setTempPriceData(
        priceData[date] ? priceData[date].map(v => (v ? Number(v) : undefined)) : new Array(96).fill(undefined)
      );
    }
  }, [selectedDates, priceData]);

  // 全选/取消全选当月
  const selectAllMonth = useCallback(() => {
    if (disabled) return;
    const monthDates = getMonthDates(currentMonth)
      .filter(isDateInContract)
      .map(d => d.format('YYYY-MM-DD'));

    setSelectedDates(prev => {
      // 检查当前月份所有日期是否都已选中
      const allSelected = monthDates.every(d => prev.includes(d));
      if (allSelected) {
        // 已全选，则取消选中当前月份所有日期
        return prev.filter(d => !monthDates.includes(d));
      } else {
        // 未全选，则选中当前月份所有日期
        const newDates = new Set(prev);
        monthDates.forEach(d => newDates.add(d));
        return Array.from(newDates);
      }
    });
  }, [currentMonth, getMonthDates, isDateInContract, disabled]);

  // 切换月份
  const changeMonth = useCallback((direction: 'prev' | 'next') => {
    setCurrentMonth(prev => (direction === 'prev' ? prev.subtract(1, 'month') : prev.add(1, 'month')));
  }, []);

  // 清空表格数据
  const clearSelection = useCallback(() => {
    setTempPriceData(new Array(96).fill(undefined));
  }, []);

  // 复制到选中月
  const copyToSelectedMonth = useCallback(() => {
    if (selectedDates.length === 0) return;
    // 检查是否所有时段都已填写
    const allFilled = tempPriceData.every(v => v !== undefined);
    if (!allFilled) {
      Modal.warning({
        title: '提示',
        content: '请配置完整96个时段的单价后再复制',
      });
      return;
    }
    setCopyToMonthModalVisible(true);
  }, [selectedDates, tempPriceData]);

  // 复制到所选月接口
  const { runAsync: copyToMonth } = useRequest(
    (params: Parameters<typeof apiV2ElectricityContractMarketPriceCopyToMonthPost>[0]) =>
      apiV2ElectricityContractMarketPriceCopyToMonthPost(params),
    { manual: true }
  );

  // 确认复制到选中月
  const handleCopyToMonthOk = useCallback(
    async (year: number, months: number[]) => {
      // 构建请求参数
      const params = {
        electricityContractId: contract?.id,
        sourcePeriods: tempPriceData.map((price, index) => ({
          period: index + 1,
          price: price!,
        })),
        targetYearMonths: months.map(month => dayjs(`${year}-${month}-01`).startOf('month').valueOf()),
      };

      await copyToMonth(params);
      message.success('复制成功');
      setCopyToMonthModalVisible(false);

      // 重新请求详情接口
      if (contract?.id) {
        fetchMonthDetail({
          electricityContractId: contract.id,
          monthTimestamp: currentMonth.startOf('month').valueOf(),
        });
      }
    },
    [tempPriceData, contract?.id, copyToMonth, fetchMonthDetail, currentMonth]
  );

  // 编辑按钮点击
  const handleEdit = useCallback(() => {
    setIsEditing(true);
  }, []);

  // 取消编辑
  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setIsBatchMode(false);
    setSelectedDates([]);
  }, []);

  // 开始批量配置模式
  const handleStartBatchMode = useCallback(() => {
    setIsBatchMode(true);
    // 如果有选中日期，加载第一个选中日期的数据
    if (selectedDates.length > 0) {
      const firstDate = selectedDates[0];
      setTempPriceData(
        priceData[firstDate]
          ? priceData[firstDate].map(v => (v ? Number(v) : undefined))
          : new Array(96).fill(undefined)
      );
    }
  }, [selectedDates, priceData]);

  // 更新价格
  const updatePrice = useCallback((index: number, value: number | null) => {
    setTempPriceData(prev => {
      const newData = [...prev];
      newData[index] = value ?? undefined;
      return newData;
    });
  }, []);

  // 表格列定义
  const columns = useMemo(
    () => [
      {
        title: '序号',
        dataIndex: 'index',
        width: 120,
        align: 'center' as const,
        render: (_: unknown, __: unknown, index: number) => index + 1,
      },
      {
        title: '时段',
        dataIndex: 'timeSlot',
        width: 240,
        align: 'center' as const,
      },
      {
        title: '单价(元/kWh)',
        dataIndex: 'price',
        render: (_: unknown, __: unknown, index: number) => (
          <InputNumber
            min={0}
            precision={contract?.precision ?? 4}
            placeholder="请输入"
            value={tempPriceData[index]}
            onChange={v => updatePrice(index, v)}
            style={{ width: 260 }}
            disabled={!isEditing}
          />
        ),
      },
    ],
    [isEditing, tempPriceData, updatePrice, contract?.precision]
  );

  // 表格数据
  const tableData = useMemo(
    () =>
      timeSlots.map((slot, index) => ({
        key: index,
        timeSlot: slot,
        price: tempPriceData[index],
      })),
    [tempPriceData]
  );

  // 保存批量配置
  const { runAsync: saveBatchPrice, loading: saveLoading } = useRequest(
    (params: Parameters<typeof apiV2ElectricityContractMarketPriceBatchAddPost>[0]) =>
      apiV2ElectricityContractMarketPriceBatchAddPost(params),
    { manual: true }
  );

  const handleBatchSave = useCallback(async () => {
    if (selectedDates.length === 0) return;
    // 检查是否所有时段都已填写
    const allFilled = tempPriceData.every(v => v !== undefined);
    if (!allFilled) {
      Modal.warning({
        title: '提示',
        content: '请配置完整96个时段的单价后再保存',
      });
      return;
    }
    // 构建请求参数
    const params = {
      electricityContractId: contract?.id,
      days: selectedDates.map(dateStr => ({
        date: dayjs(dateStr).valueOf(),
        periods: tempPriceData.map((price, index) => ({
          period: index + 1,
          price: price!,
        })),
      })),
    };

    await saveBatchPrice(params);
    message.success('保存成功');
    setIsEditing(false);
    setIsBatchMode(false);
    // 重新请求详情接口保证数据一致性
    if (contract?.id) {
      fetchMonthDetail({
        electricityContractId: contract.id,
        monthTimestamp: currentMonth.startOf('month').valueOf(),
      });
    }
  }, [selectedDates, tempPriceData, contract?.id, saveBatchPrice, fetchMonthDetail, currentMonth]);

  // 取消批量配置
  const handleBatchCancel = useCallback(() => {
    setIsBatchMode(false);
    if (selectedDates.length > 0) {
      const firstDate = selectedDates[0];
      setTempPriceData(
        priceData[firstDate]
          ? priceData[firstDate].map(v => (v ? Number(v) : undefined))
          : new Array(96).fill(undefined)
      );
    }
  }, [selectedDates, priceData]);

  return (
    <SubContent
      title={
        <Space size={8}>
          <span>费率</span>
          {isEditing ? (
            <Space size={8}>
              <Button
                type="primary"
                onClick={handleBatchSave}
                disabled={selectedDates.length === 0}
                loading={saveLoading}
              >
                保存
              </Button>
              <Button onClick={handleCancelEdit}>取消</Button>
            </Space>
          ) : (
            !disabled && (
              <Button type="primary" onClick={handleEdit}>
                编辑
              </Button>
            )
          )}
        </Space>
      }
    >
      <Row gutter={0} wrap={false}>
        {/* 左侧日历 */}
        <Col flex="none">
          <div className={styles.calendarWrapper}>
            {/* 月份导航 */}
            <div className={styles.monthNav}>
              <Button icon={<LeftOutlined />} onClick={() => changeMonth('prev')} />
              <span className={styles.monthTitle}>
                {currentMonth.year()}年{currentMonth.month() + 1}月
              </span>
              <Button icon={<RightOutlined />} onClick={() => changeMonth('next')} />
            </div>

            {/* 星期标题 */}
            <div className={styles.weekHeader}>
              {['日', '一', '二', '三', '四', '五', '六'].map(day => (
                <div key={day} className={styles.weekDay}>
                  {day}
                </div>
              ))}
            </div>

            {/* 日期网格 */}
            <div className={styles.dateGrid}>
              {calendarGrid.map((date, index) => {
                if (!date) {
                  // 空白占位
                  return <div key={index} className={styles.dateCellEmpty} />;
                }
                const dateStr = date.format('YYYY-MM-DD');
                const isSelected = selectedDates.includes(dateStr);
                // 只有在有合同的情况下才判断是否在范围内
                const isOutOfRange = contract && !isDateInContract(date);
                const isConfigured = configuredDates[dateStr];

                // Tooltip 内容
                const getTooltipContent = () => {
                  if (isOutOfRange) {
                    return '未在合同有效期内';
                  }
                  if (isConfigured) {
                    return <span style={{ color: '#52C41A' }}>已配置</span>;
                  }
                  return '未配置';
                };

                return (
                  <Tooltip key={index} title={getTooltipContent()}>
                    <div
                      className={`${styles.dateCell} ${isSelected ? styles.selected : ''} ${
                        isOutOfRange ? styles.disabled : ''
                      } ${isConfigured ? styles.configured : ''}`}
                      onClick={() => toggleDate(date)}
                    >
                      <span>{date.date()}</span>
                    </div>
                  </Tooltip>
                );
              })}
            </div>

            {/* 全选当月和已选天数 */}
            <div className={styles.selectAllWrapper}>
              {isBatchMode && (
                <Button type="link" onClick={selectAllMonth} disabled={disabled}>
                  全选当月
                </Button>
              )}
              {selectedDates.length > 0 && (
                <span className={styles.selectedCount}>
                  已选中<span className={styles.countNum}>{selectedDates.length}</span>天
                </span>
              )}
            </div>

            {/* 批量配置按钮 */}
            <div className={styles.batchButtons}>
              {isBatchMode ? (
                <>
                  <Button type="primary" onClick={handleBatchSave} disabled={disabled || selectedDates.length === 0}>
                    保存批量配置
                  </Button>
                  <Button onClick={handleBatchCancel} disabled={disabled}>
                    取消
                  </Button>
                </>
              ) : (
                <Button type="primary" onClick={handleStartBatchMode} disabled={disabled || !isEditing}>
                  批量配置
                </Button>
              )}
            </div>

            {/* 提示信息 */}
            <div className={styles.tipInfo}>
              <InfoCircleOutlined style={{ fontSize: '14px', marginRight: '6px', color: 'var(--mx-warning-color)' }} />
              <span>左侧选中需要批量配置的日期后，在右侧填写并保存，所填时段电价将批量覆盖所选日期</span>
            </div>
          </div>
        </Col>

        {/* 右侧表格 */}
        <Col flex="auto">
          <div className={styles.tableWrapper}>
            {selectedDates.length === 0 ? (
              <div className={styles.emptyWrapper}>
                <Empty description="请在左侧选择日期后查看" />
              </div>
            ) : (
              <>
                {/* 选中日期显示和右上角按钮 */}
                <div className={styles.tableHeader}>
                  <span className={styles.selectedDatesDisplay}>
                    {selectedDates.length <= 3
                      ? selectedDates.join('，')
                      : `${selectedDates.slice(0, 3).join('，')}等等`}
                  </span>
                  <Space>
                    <PopConfirm title="确认清空？" onConfirm={clearSelection} disabled={disabled || !isEditing}>
                      <Button disabled={disabled || !isEditing}>清空</Button>
                    </PopConfirm>
                    <Button
                      onClick={copyToSelectedMonth}
                      disabled={disabled || !isEditing || isBatchMode || selectedDates.length === 0}
                    >
                      复制到选中月
                    </Button>
                  </Space>
                </div>

                {/* 可编辑表格 */}
                <Table rowKey="key" columns={columns} dataSource={tableData} scroll={{ y: 500 }} pagination={false} />
              </>
            )}
          </div>
        </Col>
      </Row>

      {/* 复制到所选月弹窗 */}
      <CopyToMonthModal
        visible={copyToMonthModalVisible}
        onCancel={() => setCopyToMonthModalVisible(false)}
        onOk={handleCopyToMonthOk}
        contractStartDate={contract?.startDate}
        contractEndDate={contract?.endDate}
      />
    </SubContent>
  );
};

export default StateGridRate;
